import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { getOrder, updateOrderStatus } from '@/lib/api';
import { 
  ArrowLeft, 
  Package, 
  Building2, 
  Calendar,
  MapPin,
  Loader2,
  CheckCircle,
  XCircle,
  Truck,
  DollarSign,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  pendiente: { label: 'Pendiente', labelEn: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  confirmado: { label: 'Confirmado', labelEn: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  en_produccion: { label: 'En Producción', labelEn: 'In Production', color: 'bg-purple-100 text-purple-800' },
  enviado: { label: 'Enviado', labelEn: 'Shipped', color: 'bg-indigo-100 text-indigo-800' },
  entregado: { label: 'Entregado', labelEn: 'Delivered', color: 'bg-emerald-100 text-emerald-800' },
  cancelado: { label: 'Cancelado', labelEn: 'Cancelled', color: 'bg-red-100 text-red-800' },
};

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [responseNote, setResponseNote] = useState('');

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const data = await getOrder(orderId);
        setOrder(data);
      } catch (error) {
        console.error('Error fetching order:', error);
        toast.error(isUSA ? 'Error loading order' : 'Error al cargar pedido');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId, isUSA]);

  const isSeller = user?.company_id === order?.seller_company_id;
  const isBuyer = user?.company_id === order?.buyer_company_id;

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await updateOrderStatus(orderId, newStatus);
      setOrder(prev => ({ ...prev, status: newStatus }));
      toast.success(isUSA ? 'Status updated' : 'Estado actualizado');
    } catch (error) {
      toast.error(isUSA ? 'Error updating status' : 'Error al actualizar estado');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">{isUSA ? 'Order not found' : 'Pedido no encontrado'}</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isUSA ? 'Go back' : 'Volver'}
        </Button>
      </div>
    );
  }

  const status = statusConfig[order.status] || statusConfig.pendiente;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="order-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">
              {isUSA ? 'Order' : 'Pedido'}
            </h1>
            <Badge className={status.color}>
              {isUSA ? status.labelEn : status.label}
            </Badge>
          </div>
          <p className="text-sm text-slate-500">ID: {order.order_id}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                {isUSA ? 'Order Details' : 'Detalles del Pedido'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-500">{isUSA ? 'Product' : 'Producto'}</label>
                  <p className="font-medium">{order.product_name || order.product_id}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-500">{isUSA ? 'Quantity' : 'Cantidad'}</label>
                  <p className="font-medium">{order.quantity?.toLocaleString()} {order.unit || 'metros'}</p>
                </div>
                {order.unit_price && (
                  <div>
                    <label className="text-sm text-slate-500">{isUSA ? 'Unit Price' : 'Precio Unitario'}</label>
                    <p className="font-medium">${order.unit_price} MXN</p>
                  </div>
                )}
                {order.total_price && (
                  <div>
                    <label className="text-sm text-slate-500">{isUSA ? 'Total' : 'Total'}</label>
                    <p className="font-medium text-lg text-emerald-600">${order.total_price?.toLocaleString()} MXN</p>
                  </div>
                )}
                {order.color && (
                  <div>
                    <label className="text-sm text-slate-500">{isUSA ? 'Color' : 'Color'}</label>
                    <p className="font-medium">{order.color}</p>
                  </div>
                )}
                {order.delivery_date && (
                  <div>
                    <label className="text-sm text-slate-500">{isUSA ? 'Delivery Date' : 'Fecha de Entrega'}</label>
                    <p className="font-medium">
                      {new Date(order.delivery_date).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
                    </p>
                  </div>
                )}
              </div>
              {order.notes && (
                <div>
                  <label className="text-sm text-slate-500">{isUSA ? 'Notes' : 'Notas'}</label>
                  <p className="mt-1 text-slate-700">{order.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Seller Actions */}
          {isSeller && order.status === 'pendiente' && (
            <Card>
              <CardHeader>
                <CardTitle>{isUSA ? 'Respond to Order' : 'Responder Pedido'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder={isUSA ? 'Additional notes...' : 'Notas adicionales...'}
                  value={responseNote}
                  onChange={(e) => setResponseNote(e.target.value)}
                />
                <div className="flex gap-3">
                  <Button 
                    onClick={() => handleStatusUpdate('confirmado')}
                    disabled={updating}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    {isUSA ? 'Confirm Order' : 'Confirmar Pedido'}
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => handleStatusUpdate('cancelado')}
                    disabled={updating}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {isUSA ? 'Reject' : 'Rechazar'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Production -> Shipped */}
          {isSeller && order.status === 'confirmado' && (
            <Card>
              <CardContent className="pt-6">
                <Button 
                  onClick={() => handleStatusUpdate('en_produccion')}
                  disabled={updating}
                  className="w-full"
                >
                  <Package className="w-4 h-4 mr-2" />
                  {isUSA ? 'Start Production' : 'Iniciar Producción'}
                </Button>
              </CardContent>
            </Card>
          )}

          {isSeller && order.status === 'en_produccion' && (
            <Card>
              <CardContent className="pt-6">
                <Button 
                  onClick={() => handleStatusUpdate('enviado')}
                  disabled={updating}
                  className="w-full"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  {isUSA ? 'Mark as Shipped' : 'Marcar como Enviado'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Buyer Confirm Delivery */}
          {isBuyer && order.status === 'enviado' && (
            <Card>
              <CardContent className="pt-6">
                <Button 
                  onClick={() => handleStatusUpdate('entregado')}
                  disabled={updating}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {isUSA ? 'Confirm Delivery' : 'Confirmar Entrega'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{isUSA ? 'Order Info' : 'Info del Pedido'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'Buyer' : 'Comprador'}</p>
                  <p className="font-medium">{order.buyer_company_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'Seller' : 'Vendedor'}</p>
                  <p className="font-medium">{order.seller_company_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'Order Date' : 'Fecha de Pedido'}</p>
                  <p className="font-medium">
                    {new Date(order.created_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
                  </p>
                </div>
              </div>
              {order.shipping_address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Shipping Address' : 'Dirección de Envío'}</p>
                    <p className="font-medium">{order.shipping_address}</p>
                  </div>
                </div>
              )}
              {order.total_price && (
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Total Amount' : 'Monto Total'}</p>
                    <p className="font-medium text-lg text-emerald-600">${order.total_price?.toLocaleString()} MXN</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
